import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import AgentInstallManager from '../components/AgentInstallManager'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { useAuth } from '../contexts/AuthContext'
import { signOut } from '../lib/supabase'

// ============================================================================
// Page: /install/claude-code
//
// One-line `curl | bash` install flow for the Claude Code auto check-in
// hook (2026-09-06 redesign) — replaces the earlier "paste this 9-step
// prompt into your Claude Code session" flow. Gated behind auth via
// <ProtectedRoute> in App.tsx (see the route registration) rather than a
// bespoke redirect here — this reuses the same signed-in check already used
// for /dashboard.
//
// Contract with mint-install-token (redesigned 2026-09-06):
//   Response shape is { success: true, data: { claim_code, client_id, team_id } }.
//   claim_code is short-lived/single-use, NOT the real install token — see
//   claim-install-token's header comment for why. install.sh redeems it for
//   the real token server-side.
//
// Phase 4: hook.sh ships at public/install/claude-code/hook.sh.
// 2026-09-06: install.sh ships alongside it at the same path.
//
// Hook URL history, corrected twice now — read this before changing it
// again:
//   - Originally assumed the bare syncsimple.ai domain + Vercel (spec error).
//   - Then set to app.syncsimple.ai, reasoning from this app's own code
//     (vite.config.ts allowedHosts, the OAuth redirect base in
//     lib/supabase.ts, the CTA link in PricingPage.tsx) — accurate for where
//     this app resolves to *once deployed*, but app.syncsimple.ai is not
//     live yet.
//   - Then: the live UAT Vercel URL, read from VITE_HOOK_URL with that UAT
//     URL as the fallback default, because app.syncsimple.ai wasn't live yet
//     and this repo's own `npm run deploy` ships to Cloudflare Pages via
//     wrangler, not Vercel — UAT and prod deploy targets differed, which is
//     why this needed to be an env var rather than hardcoded twice.
//   - Current (2026-09-06): confirmed app.syncsimple.ai is live and serving
//     hook.sh, so DEFAULT_HOOK_URL/DEFAULT_INSTALL_URL in agentInstall.ts
//     (and install.sh's own hardcoded HOOK_URL) now point there directly.
//     VITE_HOOK_URL/VITE_INSTALL_URL still exist as overrides — set them if
//     you ever need to point a build back at a UAT/staging host.
//
// Sidebar shell: wrapped in the same AppSidebar/TopBar as TeamDashboard (see
// [[syncsimple_frontend_dashboard_v2_restyle]]) so this reads as a page
// inside the app rather than a bare standalone screen. The sidebar's nav
// callbacks (Dashboard/Standups/Agents/Team Management/Billing) all just
// navigate back to the team dashboard here — this page doesn't have those
// sections itself, and deep-linking into a scroll position from a fresh
// page load isn't worth the complexity for a 2-page app.
// ============================================================================

const InstallClaudeCode: React.FC = () => {
  const { userTeams } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const queryTeamId = new URLSearchParams(location.search).get('teamId')
  const fallbackTeamId = userTeams?.[0]?.team_id ?? null
  const teamId = queryTeamId ?? fallbackTeamId
  const teamName = userTeams?.find((membership) => membership.team_id === teamId)?.teams?.name

  const goToDashboard = () => {
    if (teamId) navigate(`/dashboard/team/${teamId}`)
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  return (
    <SidebarProvider>
      <AppSidebar
        activeSection="onboarding"
        onNavigate={goToDashboard}
        onOpenTeamManagement={() => navigate(`/dashboard/team/${teamId}/team-management`)}
        onOpenBilling={() => navigate(`/dashboard/team/${teamId}?open=billing`)}
        teamId={teamId ?? undefined}
      />
      <SidebarInset>
        <TopBar
          teamId={teamId ?? undefined}
          teamName={teamName ?? undefined}
          onLogout={handleLogout}
          sectionLabel="Onboard Connector"
        />
        <main className="space-y-6 bg-background p-4 sm:p-6">
          <div className="mx-auto max-w-2xl space-y-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Install Claude Code auto check-in</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Generate a one-time install command. Run it in your own terminal to wire up automatic
                standup check-ins — no need to paste anything into Claude Code itself.
              </p>
            </div>
            {teamId ? (
              <AgentInstallManager teamId={teamId} defaultExpanded />
            ) : (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                We couldn&apos;t determine which team to install this connector into. Open the install flow
                from a team dashboard, or pass `?teamId=...` in the URL.
              </div>
            )}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default InstallClaudeCode
